import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, LinearProgress, Stack, Chip, Grid,
  FormControl, InputLabel, Select, MenuItem, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon, ViewList as ListIcon, AccountTree as GroupIcon, FileDownload as FileDownloadIcon, Flag as MilestoneIcon, PlayArrow as NextActionIcon } from '@mui/icons-material';
import { projectsApi, driversApi, workItemsApi, exportApi } from '../api/services';
import { useAuth } from '../auth/AuthProvider';
import { useNavigate } from 'react-router-dom';
import type {
  Project, ProjectStatus, RAGStatus, AnnualStrategicDriver, WorkItem, ProjectOutcome,
} from '../types/models';
import HelpPanel from '../components/HelpPanel';
import DriverPill from '../components/DriverPill';
import {
  selectNextMilestone,
  selectNextAction,
  rollupRag,
  describeMilestone,
} from '../utils/projectPortfolio';

const projectStatuses: ProjectStatus[] = ['Planning', 'Active', 'OnHold', 'Completed', 'Cancelled'];

const ragColorMap: Record<RAGStatus, 'success' | 'warning' | 'error'> = {
  Green: 'success',
  Amber: 'warning',
  Red: 'error',
};

const statusColorMap: Record<ProjectStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  Planning: 'info',
  Active: 'success',
  OnHold: 'warning',
  Completed: 'default',
  Cancelled: 'error',
};

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [drivers, setDrivers] = useState<AnnualStrategicDriver[]>([]);
  const [allWorkItems, setAllWorkItems] = useState<WorkItem[]>([]);
  const [allOutcomes, setAllOutcomes] = useState<Record<string, ProjectOutcome[]>>({});
  const [loading, setLoading] = useState(true); // eslint-disable-line @typescript-eslint/no-unused-vars

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterDriver, setFilterDriver] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [groupByDriver, setGroupByDriver] = useState<'grouped' | 'flat'>('grouped');

  // New project dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState<Partial<Project>>({
    title: '',
    description: '',
    status: 'Planning',
    strategicDriverId: '',
    startDate: '',
    targetEndDate: '',
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [projectsData, driversData, workItemsData] = await Promise.all([
        projectsApi.getAll(),
        driversApi.getActive(),
        workItemsApi.getAll(),
      ]);
      setProjects(projectsData);
      setDrivers(driversData);
      setAllWorkItems(workItemsData);

      // Load outcomes for each project
      const outcomesByProject: Record<string, ProjectOutcome[]> = {};
      await Promise.all(
        projectsData.map(async (p) => {
          try {
            outcomesByProject[p.id] = await projectsApi.getOutcomes(p.id);
          } catch {
            outcomesByProject[p.id] = [];
          }
        })
      );
      setAllOutcomes(outcomesByProject);
    } catch (err) {
      console.error('Failed to load projects data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter projects
  const filteredProjects = useMemo(() => {
    let result = projects.filter(p => !p.isArchived);
    if (filterStatus) result = result.filter(p => p.status === filterStatus);
    if (filterDriver) result = result.filter(p => p.strategicDriverId === filterDriver);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.title.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [projects, filterStatus, filterDriver, searchQuery]);

  // Group by driver
  const groupedProjects = useMemo(() => {
    if (groupByDriver === 'flat') return null;
    const groups: Record<string, { driver: AnnualStrategicDriver | null; projects: Project[] }> = {};
    // Create groups for each driver
    drivers.forEach(d => {
      groups[d.id] = { driver: d, projects: [] };
    });
    groups['_unlinked'] = { driver: null, projects: [] };

    filteredProjects.forEach(p => {
      const key = p.strategicDriverId && groups[p.strategicDriverId] ? p.strategicDriverId : '_unlinked';
      groups[key].projects.push(p);
    });

    // Only return groups that have projects
    return Object.entries(groups).filter(([, g]) => g.projects.length > 0);
  }, [filteredProjects, drivers, groupByDriver]);

  // Work item stats per project
  const projectWorkItemStats = useMemo(() => {
    const stats: Record<string, { open: number; total: number }> = {};
    allWorkItems.forEach(wi => {
      if (wi.projectId) {
        if (!stats[wi.projectId]) stats[wi.projectId] = { open: 0, total: 0 };
        stats[wi.projectId].total++;
        if (!['Done', 'Cancelled'].includes(wi.status)) stats[wi.projectId].open++;
      }
    });
    return stats;
  }, [allWorkItems]);

  // Outcome stats per project
  const projectOutcomeStats = useMemo(() => {
    const stats: Record<string, { achieved: number; total: number }> = {};
    Object.entries(allOutcomes).forEach(([projectId, outcomes]) => {
      stats[projectId] = {
        achieved: outcomes.filter(o => o.status === 'Achieved').length,
        total: outcomes.length,
      };
    });
    return stats;
  }, [allOutcomes]);

  const getDriver = (driverId?: string) =>
    driverId ? drivers.find(d => d.id === driverId) ?? null : null;

  const getDaysRemaining = (targetEndDate?: string) => {
    if (!targetEndDate) return null;
    const target = new Date(targetEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    const diff = Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const handleCreateProject = async () => {
    if (!newProject.title) return;
    try {
      const payload = {
        ...newProject,
        strategicDriverId: newProject.strategicDriverId || undefined,
        startDate: newProject.startDate || undefined,
        targetEndDate: newProject.targetEndDate || undefined,
      };
      await projectsApi.create(payload);
      setDialogOpen(false);
      setNewProject({ title: '', description: '', status: 'Planning', strategicDriverId: '', startDate: '', targetEndDate: '' });
      loadData();
    } catch (err) {
      console.error('Failed to create project', err);
    }
  };

  // Portfolio health rollup — drives the banner above the filter bar.
  const portfolioRag = useMemo(() => rollupRag(filteredProjects), [filteredProjects]);

  const renderProjectCard = (project: Project) => {
    const driver = getDriver(project.strategicDriverId);
    const daysRemaining = getDaysRemaining(project.targetEndDate);
    const wiStats = projectWorkItemStats[project.id] || { open: 0, total: 0 };
    const outcomeStats = projectOutcomeStats[project.id] || { achieved: 0, total: 0 };
    const nextMilestone = selectNextMilestone(allOutcomes[project.id] || []);
    const projectItems = allWorkItems.filter(w => w.projectId === project.id);
    const nextAction = selectNextAction(projectItems);

    return (
      <Grid size={{ xs: 12, md: 6, lg: 4 }} key={project.id}>
        <Card
          sx={{
            height: '100%',
            cursor: 'pointer',
            borderLeft: 3,
            borderColor: ragColorMap[project.ragStatus] + '.main',
            '&:hover': { bgcolor: 'action.hover' },
          }}
          onClick={() => navigate(`/projects/${project.id}`)}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="h6" sx={{ flex: 1, mr: 1 }}>{project.title}</Typography>
              <Stack direction="row" spacing={0.5}>
                <Chip label={project.ragStatus} size="small" color={ragColorMap[project.ragStatus]} />
                <Chip label={project.status} size="small" variant="outlined" color={statusColorMap[project.status]} />
              </Stack>
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <DriverPill driver={driver} />
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Progress</Typography>
                <Typography variant="caption" fontWeight={600}>{project.progressPercentage}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={project.progressPercentage}
                sx={{ height: 6, borderRadius: 3 }}
                color={project.ragStatus === 'Red' ? 'error' : project.ragStatus === 'Amber' ? 'warning' : 'success'}
              />
            </Box>

            {/* Next milestone + next action — the "what's happening on this
                project next?" glance pair. Either may be missing (new
                project with nothing scheduled yet) and we render only what
                we have. */}
            {nextMilestone && (
              <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mb: 0.75 }}>
                <MilestoneIcon sx={{
                  fontSize: 16,
                  mt: '2px',
                  color: nextMilestone.overdue ? 'error.main' : nextMilestone.daysUntil !== null && nextMilestone.daysUntil <= 7 ? 'warning.main' : 'text.secondary',
                }} />
                <Typography variant="caption" sx={{
                  color: nextMilestone.overdue ? 'error.main' : 'text.secondary',
                  fontWeight: nextMilestone.overdue ? 600 : 400,
                  lineHeight: 1.3,
                }}>
                  {describeMilestone(nextMilestone)}
                </Typography>
              </Stack>
            )}
            {nextAction && (
              <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mb: 1 }}>
                <NextActionIcon sx={{ fontSize: 16, mt: '2px', color: 'primary.main' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.3 }}>
                  Next action: {nextAction.title}
                </Typography>
              </Stack>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {outcomeStats.achieved}/{outcomeStats.total} outcomes &middot; {wiStats.open} open items
            </Typography>

            {project.targetEndDate && daysRemaining !== null && (
              <Typography
                variant="caption"
                sx={{
                  color: daysRemaining < 0 ? 'error.main' : daysRemaining <= 7 ? 'warning.main' : 'text.secondary',
                  fontWeight: daysRemaining < 0 ? 600 : 400,
                }}
              >
                {daysRemaining < 0
                  ? `${Math.abs(daysRemaining)} days overdue`
                  : daysRemaining === 0
                    ? 'Due today'
                    : `${daysRemaining} days remaining`}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const handleExportCsv = async () => {
    try {
      const response = await exportApi.downloadProjectsCsv();
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `projects-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Failed to export projects.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">Projects</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportCsv}>
            Export CSV
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            New Project
          </Button>
        </Stack>
      </Box>

      <HelpPanel title="About Projects" storageKey="projects">
        Projects connect your strategic drivers to daily execution. Track outcomes, indicators, and time invested.
      </HelpPanel>

      {/* Portfolio RAG rollup — answers "how healthy is the portfolio right
          now?" above the detail grid. Only shown when there are projects to
          summarise (otherwise the empty state below carries the message). */}
      {portfolioRag.total > 0 && (
        <Card sx={{ mb: 2, borderLeft: 3, borderColor: portfolioRag.red > 0 ? 'error.main' : portfolioRag.amber > 0 ? 'warning.main' : 'success.main' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }} alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" useFlexGap>
              <Typography variant="body2" fontWeight={600}>
                Portfolio health
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" color="error" label={`${portfolioRag.red} Red`} />
                <Chip size="small" color="warning" label={`${portfolioRag.amber} Amber`} />
                <Chip size="small" color="success" label={`${portfolioRag.green} Green`} />
                <Typography variant="caption" color="text.secondary">
                  {portfolioRag.healthPercent}% green across {portfolioRag.total} active
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Search projects..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, fontSize: 18, color: 'text.secondary' }} /> }}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {projectStatuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Strategic Driver</InputLabel>
          <Select value={filterDriver} label="Strategic Driver" onChange={e => setFilterDriver(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {drivers.map(d => <MenuItem key={d.id} value={d.id}>{d.title}</MenuItem>)}
          </Select>
        </FormControl>
        <ToggleButtonGroup
          value={groupByDriver}
          exclusive
          onChange={(_, v) => { if (v) setGroupByDriver(v); }}
          size="small"
        >
          <ToggleButton value="grouped" title="Group by driver"><GroupIcon fontSize="small" /></ToggleButton>
          <ToggleButton value="flat" title="Flat list"><ListIcon fontSize="small" /></ToggleButton>
        </ToggleButtonGroup>
        {(searchQuery || filterStatus || filterDriver) && (
          <Button size="small" onClick={() => { setSearchQuery(''); setFilterStatus(''); setFilterDriver(''); }}>
            Clear
          </Button>
        )}
      </Stack>

      {/* Projects */}
      {filteredProjects.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 1 }}>No projects found</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Create your first project
          </Button>
        </Card>
      ) : groupByDriver === 'grouped' && groupedProjects ? (
        groupedProjects.map(([key, group]) => (
          <Box key={key} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1.5, color: 'text.secondary' }}>
              {group.driver ? group.driver.title : 'Other Projects'}
            </Typography>
            <Grid container spacing={2}>
              {group.projects.map(renderProjectCard)}
            </Grid>
          </Box>
        ))
      ) : (
        <Grid container spacing={2}>
          {filteredProjects.map(renderProjectCard)}
        </Grid>
      )}

      {/* New Project Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Project</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              fullWidth
              required
              value={newProject.title || ''}
              onChange={e => setNewProject(prev => ({ ...prev, title: e.target.value }))}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={newProject.description || ''}
              onChange={e => setNewProject(prev => ({ ...prev, description: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Strategic Driver</InputLabel>
              <Select
                value={newProject.strategicDriverId || ''}
                label="Strategic Driver"
                onChange={e => setNewProject(prev => ({ ...prev, strategicDriverId: e.target.value }))}
              >
                <MenuItem value="">None</MenuItem>
                {drivers.map(d => <MenuItem key={d.id} value={d.id}>{d.title}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={newProject.status || 'Planning'}
                label="Status"
                onChange={e => setNewProject(prev => ({ ...prev, status: e.target.value as ProjectStatus }))}
              >
                {projectStatuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newProject.startDate || ''}
                onChange={e => setNewProject(prev => ({ ...prev, startDate: e.target.value }))}
              />
              <TextField
                label="Target End Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newProject.targetEndDate || ''}
                onChange={e => setNewProject(prev => ({ ...prev, targetEndDate: e.target.value }))}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateProject} disabled={!newProject.title}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
